import { hrefOf } from '../overview/href'
import { useQuery } from '@tanstack/react-query'
import { useRef, useState, type KeyboardEvent } from 'react'
import {
  acceptOwnerSuggestion,
  assignOwner,
  dismissOwnerSuggestion,
  proposeRename,
  quickAddOwner,
  removeDocument,
  saveAlias,
  summaryQuery,
  type DeviceSummaryPage,
  type OwnerOption,
  type OwnerSuggestion,
  type SummaryDocument,
  type SummaryFact,
  type SummaryTile,
} from '../api/devices'
import { moduleOf, shellQuery } from '../api/shell'
import { Modal } from '../ui/Modal'
import { newAlert, toast } from '../ui/notify'
import { usePageStyles } from '../ui/usePageStyles'
import type { ScreenProps } from './screens'
import { DevicePartOfApps } from './DevicePartOfApps'
import { DeviceSqlDatabases } from './DeviceSqlDatabases'

/**
 * Fila Summary a fisei - inventoree/devices/others/summary.php cu partialul
 * _partials/device_rename.php si gesturile din device_owner.js si
 * device_rename-1.js: cele opt casute in doua randuri, numele si proprietarul
 * pe randurile lor cu schimbarea pe loc, grila de fapte, codul QR, poza cu
 * fereastra ei, documentele cu descarcare si scoatere.
 *
 * Legaturile `more` de pe casute deschid fila IN PAGINA, cum promite
 * comentariul din summary.php ("action_button ... deschide fila IN PAGINA"):
 * prin `open()` al fisei, care aprinde si optiunea din meniu.
 */

const STYLES = ['css/device_details.css']

function Loading() {
  return (
    <div className="spinner rectangle-bounce">
      <div className="rect1 rect-blue rect-margin-fix"></div>
      <div className="rect2 rect-blue rect-margin-fix"></div>
      <div className="rect3 rect-blue rect-margin-fix"></div>
      <div className="rect4 rect-blue rect-margin-fix"></div>
      <div className="rect5 rect-blue rect-margin-fix"></div>
    </div>
  )
}

/* ------------------------------------------------------------------------- */
/* casutele                                                                  */
/* ------------------------------------------------------------------------- */

function Tile({ tile, id, open }: { tile: SummaryTile; id: number; open(href: string): void }) {
  return (
    <div className="sum-tile">
      <div className="dashboard-stat brown">
        <div className="visual">
          <i className={tile.icon}></i>
        </div>
        <div className="details" title={tile.title !== '' ? tile.title : undefined}>
          <div className="number number-summary" style={{ whiteSpace: 'nowrap' }}>
            {tile.number !== '' ? tile.number : <em style={{ fontSize: '70%' }}>n/a</em>}
          </div>
          <div className="desc">{tile.label}</div>
          {tile.note.trim() !== '' && <div className="sum-note" dangerouslySetInnerHTML={{ __html: tile.note }} />}
        </div>
        {tile.more.href !== '' && (
          <a
            className="more action_button"
            href={hrefOf(`${tile.more.href}${id}`) ?? '#'}
            onClick={(ev) => {
              ev.preventDefault()
              open(tile.more.href)
            }}
          >
            {tile.more.label} <i className="m-icon-swapright m-icon-white"></i>
          </a>
        )}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------------- */
/* numele masinii si redenumirea (_partials/device_rename.php)               */
/* ------------------------------------------------------------------------- */

function label(alias: string, host: string): string {
  return alias !== '' ? alias : host
}

function sublabel(alias: string, host: string): string {
  return alias !== '' && host !== '' && alias !== host ? host : ''
}

function DeviceName({ page }: { page: DeviceSummaryPage }) {
  const { id } = page.device
  const [alias, setAlias] = useState(page.device.alias)
  const host = page.device.host !== '' ? page.device.host : page.device.name
  const [open, setOpen] = useState(false)
  const [aliasValue, setAliasValue] = useState(page.rename.alias)
  const [hostValue, setHostValue] = useState(page.rename.host)
  const [busy, setBusy] = useState<'alias' | 'host' | null>(null)
  const button = useRef<HTMLButtonElement>(null)
  const aliasInput = useRef<HTMLInputElement>(null)

  const shownLabel = label(alias, host)
  const shownSub = sublabel(alias, host)

  const toggle = (yes: boolean) => {
    setOpen(yes)
    if (yes) {
      requestAnimationFrame(() => {
        aliasInput.current?.focus()
        aliasInput.current?.select()
      })
    }
  }

  const onPanelKey = (ev: KeyboardEvent<HTMLDivElement>) => {
    if (ev.key === 'Escape') {
      ev.preventDefault()
      toggle(false)
      button.current?.focus()
    }
  }

  const doSaveAlias = async () => {
    setBusy('alias')
    try {
      const result = await saveAlias(id, aliasValue.trim())
      if (result.status !== 'OK') {
        newAlert(result.message || 'The name could not be saved.', 'danger', 5000)
        return
      }
      setAlias(result.alias)
      setAliasValue(result.alias)
      toggle(false)
    } catch {
      newAlert('The name could not be saved.', 'danger', 5000)
    } finally {
      setBusy(null)
    }
  }

  const doAskRename = async () => {
    const value = hostValue.trim()
    if (value === '' || value === page.rename.host) {
      newAlert('Write a different name for the machine first.', 'warning', 4000)
      return
    }
    if (!window.confirm(`Open a change request to rename this machine to "${value}"?\n\nNothing happens until somebody approves it under Changes.`)) {
      return
    }
    setBusy('host')
    try {
      const result = await proposeRename(id, value)
      if (result.status !== 'OK') {
        newAlert(result.message || 'The change request could not be created.', 'danger', 6000)
        return
      }
      newAlert(result.message, 'success', 8000)
      toggle(false)
    } catch {
      newAlert('The change request could not be created.', 'danger', 5000)
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="dv-owner dv-name">
      <span className="dv-owner-mark" aria-hidden="true">
        <i className="fas fa-desktop"></i>
      </span>

      <div className="dv-owner-who">
        <span className="dv-k">Device name</span>
        <span className="dv-owner-name" id="dwmDeviceName">
          {shownLabel !== '' ? shownLabel : <em className="dv-na">n/a</em>}
        </span>
        {shownSub !== '' && (
          <small className="dv-sub" id="dwmDeviceHost">
            {shownSub}
          </small>
        )}
      </div>

      {page.rename.ready && (
        <>
          <button
            type="button"
            className="dv-owner-edit"
            id="dwmRename"
            ref={button}
            data-device={id}
            data-alias={alias}
            data-host={page.rename.host}
            aria-expanded={open ? 'true' : 'false'}
            onClick={() => toggle(!open)}
          >
            <i className="fas fa-pen" aria-hidden="true"></i> Change
          </button>

          <div className="dwm-rename" id="dwmRenamePanel" hidden={!open} onKeyDown={onPanelKey}>
            <div className="dwm-rename-block">
              <span className="dwm-rename-head">Name it here</span>
              <div className="dwm-rename-row">
                <input
                  type="text"
                  className="dwm-rename-input"
                  id="dwmAliasInput"
                  ref={aliasInput}
                  maxLength={120}
                  value={aliasValue}
                  placeholder={page.rename.host !== '' ? page.rename.host : 'a name of our own'}
                  onChange={(ev) => setAliasValue(ev.target.value)}
                  onKeyDown={(ev) => {
                    if (ev.key === 'Enter') {
                      ev.preventDefault()
                      void doSaveAlias()
                    }
                  }}
                />
                <button type="button" className="dwm-rename-go" id="dwmAliasSave" disabled={busy === 'alias'} onClick={() => void doSaveAlias()}>
                  Save
                </button>
              </div>
              <p className="dwm-rename-note">
                Changes what you see in the portal - lists, tickets, search. The machine is not touched, and it takes effect right away.
              </p>
            </div>

            {page.rename.family !== '' ? (
              <div className="dwm-rename-block">
                <span className="dwm-rename-head">Rename the machine</span>
                <div className="dwm-rename-row">
                  <input
                    type="text"
                    className="dwm-rename-input"
                    id="dwmHostInput"
                    maxLength={page.rename.max}
                    value={hostValue}
                    placeholder="new hostname"
                    onChange={(ev) => setHostValue(ev.target.value)}
                    onKeyDown={(ev) => {
                      if (ev.key === 'Enter') {
                        ev.preventDefault()
                        void doAskRename()
                      }
                    }}
                  />
                  <button type="button" className="dwm-rename-go is-ask" id="dwmHostAsk" disabled={busy === 'host'} onClick={() => void doAskRename()}>
                    Request
                  </button>
                </div>
                <p className="dwm-rename-note is-warn">{page.rename.warning}</p>
                <p className="dwm-rename-note">
                  Nothing runs now: this opens a change request under <a href="/changes">Changes</a>, and somebody has to approve it.
                </p>
              </div>
            ) : (
              <div className="dwm-rename-block">
                <p className="dwm-rename-note">We do not know what this machine runs, so we cannot offer to rename the machine itself - only the name above.</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------------- */
/* proprietarul (device_owner.js)                                            */
/* ------------------------------------------------------------------------- */

const NOBODY = 'Nobody is named on this device'

function optionText(o: OwnerOption): string {
  return o.name + (o.email !== '' ? ` - ${o.email}` : '')
}

function Owner({ page }: { page: DeviceSummaryPage }) {
  const { id } = page.device
  const [owner, setOwner] = useState({ id: page.owner.id, name: page.owner.name })
  const [options, setOptions] = useState(page.owner.options)
  const [chosen, setChosen] = useState(page.owner.id > 0 ? String(page.owner.id) : '')
  const [picking, setPicking] = useState(false)
  const [fresh, setFresh] = useState(false)
  const [first, setFirst] = useState('')
  const [last, setLast] = useState('')
  const [mail, setMail] = useState('')
  const [busy, setBusy] = useState<'save' | 'create' | 'suggestion' | null>(null)
  /* Insigna "Auto" (pus automat din AD) si sugestia din logari. Orice
     schimbare facuta de aici e de mana, deci le stinge pe amandoua. */
  const [autoTitle, setAutoTitle] = useState(page.owner.auto?.title ?? '')
  const [suggestion, setSuggestion] = useState<OwnerSuggestion | null>(page.owner.suggestion ?? null)
  const select = useRef<HTMLSelectElement>(null)
  const firstInput = useRef<HTMLInputElement>(null)

  const say = (text: string, bad = false) => {
    toast(text, 'Owner', bad ? 'error' : 'success')
  }

  const answerSuggestion = async (accept: boolean) => {
    setBusy('suggestion')
    try {
      const answer = accept ? await acceptOwnerSuggestion(id) : await dismissOwnerSuggestion(id)
      if (answer.status !== 'OK') {
        say(answer.message, true)
        return
      }
      if (accept && answer.owner) {
        const taken = answer.owner
        setOwner({ id: taken.id, name: taken.name })
        setChosen(String(taken.id))
        setOptions((list) => (list.some((o) => o.id === taken.id) ? list : [...list, { id: taken.id, name: taken.name, email: '' }]))
        setAutoTitle('')
      }
      setSuggestion(null)
      say(answer.message)
    } catch (err) {
      say(err instanceof Error && err.message !== '' ? err.message : 'The suggestion could not be answered.', true)
    } finally {
      setBusy(null)
    }
  }

  const open = (on: boolean) => {
    setPicking(on)
    if (on) requestAnimationFrame(() => select.current?.focus())
  }

  const cancel = () => {
    setChosen(owner.id > 0 ? String(owner.id) : '')
    setFresh(false)
    open(false)
  }

  const save = async () => {
    const now = String(owner.id)
    /* Acelasi nume nu se mai scrie: MySQL n-ar numara randul si "Save" ar parea picat. */
    if ((chosen === '' ? '0' : chosen) === now) {
      open(false)
      return
    }
    setBusy('save')
    try {
      const result = await assignOwner(id, chosen === '' ? 0 : Number(chosen))
      if (!result || !result.changed) {
        say('The owner could not be saved.', true)
        return
      }
      const picked = options.find((o) => String(o.id) === chosen)
      const name = chosen === '' ? '' : (picked ? optionText(picked) : '').split(' - ')[0].trim()
      setOwner({ id: chosen === '' ? 0 : Number(chosen), name })
      setAutoTitle('')
      if (chosen !== '') setSuggestion(null)
      open(false)
      say(name === '' ? 'Nobody is named on this device now.' : `${name} owns this device now.`)
    } catch {
      say('The owner could not be saved.', true)
    } finally {
      setBusy(null)
    }
  }

  const create = async () => {
    if (!first.trim() || !last.trim() || !mail.trim()) {
      say('First name, last name and email are required.', true)
      return
    }
    setBusy('create')
    try {
      const result = await quickAddOwner(id, first.trim(), last.trim(), mail.trim())
      if (!result || !result.id) {
        say('The person could not be added.', true)
        return
      }
      /* Ales pe loc, nu doar adaugat in lista; salvarea ramane un buton mai incolo. */
      setOptions((list) => [...list, { id: result.id, name: result.text, email: '' }])
      setChosen(String(result.id))
      setFirst('')
      setLast('')
      setMail('')
      setFresh(false)
      say(`${result.text} can be chosen now.`)
    } catch (err) {
      say(err instanceof Error && err.message !== '' ? err.message : 'The person could not be added.', true)
    } finally {
      setBusy(null)
    }
  }

  const seen = options.some((o) => o.id === owner.id)

  return (
    <div className="dv-owner" id="dvOwner" data-device={id} data-owner={owner.id}>
      <span className="dv-owner-mark" aria-hidden="true">
        <i className="fas fa-user"></i>
      </span>

      <div className="dv-owner-who">
        <span className="dv-k">Owner</span>
        <span className="dv-owner-name" id="dvOwnerName">
          {owner.id > 0 && owner.name !== '' ? owner.name : <em className="dv-na">{NOBODY}</em>}
          {owner.id > 0 && autoTitle !== '' && (
            <>
              {' '}
              <span className="od-owner-auto" id="dvOwnerAuto" title={autoTitle}>
                Auto
              </span>
            </>
          )}
        </span>
        {suggestion && (
          <div className="od-owner-suggest" id="dvOwnerSuggest">
            <i className="fas fa-lightbulb" aria-hidden="true"></i> Suggested owner: <strong>{suggestion.name}</strong>
            {` - logged in on ${suggestion.days} of the last ${suggestion.window} days`}
            {suggestion.canAccept && (
              <>
                {' '}
                <button
                  type="button"
                  className="btn btn-xs btn-theme"
                  id="dvSuggestAssign"
                  disabled={busy === 'suggestion'}
                  onClick={() => void answerSuggestion(true)}
                >
                  Assign
                </button>{' '}
                <button
                  type="button"
                  className="btn btn-xs btn-default"
                  id="dvSuggestDismiss"
                  disabled={busy === 'suggestion'}
                  onClick={() => void answerSuggestion(false)}
                >
                  Dismiss
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {page.owner.can_assign && (
        <>
          <button type="button" className="dv-owner-edit" id="dvOwnerEdit" hidden={picking} onClick={() => open(true)}>
            <i className="fas fa-pen" aria-hidden="true"></i> {owner.id > 0 ? 'Change' : 'Choose'}
          </button>

          <div className="dv-owner-pick" id="dvOwnerPick" hidden={!picking}>
            <select className="form-control input-sm" id="dvOwnerSelect" ref={select} value={chosen} onChange={(ev) => setChosen(ev.target.value)}>
              <option value="">Nobody</option>
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {optionText(o)}
                </option>
              ))}
              {!seen && owner.id > 0 && <option value={owner.id}>{owner.name !== '' ? owner.name : `#${owner.id}`}</option>}
            </select>

            <button type="button" className="btn btn-xs btn-theme" id="dvOwnerSave" disabled={busy === 'save'} onClick={() => void save()}>
              Save
            </button>
            <button type="button" className="btn btn-xs btn-default" id="dvOwnerCancel" onClick={cancel}>
              Cancel
            </button>

            <button
              type="button"
              className="dv-owner-add"
              id="dvOwnerAdd"
              onClick={() => {
                const next = !fresh
                setFresh(next)
                if (next) requestAnimationFrame(() => firstInput.current?.focus())
              }}
            >
              <i className="fas fa-plus" aria-hidden="true"></i> Add a person
            </button>

            <div className="dv-owner-new" id="dvOwnerNew" hidden={!fresh}>
              <input type="text" className="form-control input-sm" id="dvNewFirst" ref={firstInput} placeholder="First name" autoComplete="off" value={first} onChange={(ev) => setFirst(ev.target.value)} />
              <input type="text" className="form-control input-sm" id="dvNewLast" placeholder="Last name" autoComplete="off" value={last} onChange={(ev) => setLast(ev.target.value)} />
              <input type="email" className="form-control input-sm" id="dvNewEmail" placeholder="Email" autoComplete="off" value={mail} onChange={(ev) => setMail(ev.target.value)} />
              <button type="button" className="btn btn-xs btn-theme" id="dvNewCreate" disabled={busy === 'create'} onClick={() => void create()}>
                Create
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------------- */
/* faptele                                                                   */
/* ------------------------------------------------------------------------- */

function Fact({ fact }: { fact: SummaryFact }) {
  const links = fact.links ?? []
  const hasText = fact.text.trim() !== ''
  /* Textul si legaturile pot sta impreuna: "Virtual machines: 3 · a, b, c".
     "Runs on" are fie legatura spre gazda, fie textul "Unknown" cu motivul. */
  const empty = links.length === 0 && !hasText
  return (
    <div className="dv-fact">
      <span className="dv-k">{fact.label}</span>
      <span className="dv-v">
        {fact.icon !== '' && (
          <>
            <i className={fact.icon} aria-hidden="true"></i>{' '}
          </>
        )}
        {empty ? (
          <em className="dv-na">n/a</em>
        ) : (
          <>
            {hasText && <span dangerouslySetInnerHTML={{ __html: fact.text }} />}
            {hasText && links.length > 0 && ' · '}
            {links.map((l, i) => (
              <span key={l.id}>
                {i > 0 && ', '}
                <a href={`/app/inventoree/device/${l.id}`}>{l.name}</a>
              </span>
            ))}
          </>
        )}
      </span>
    </div>
  )
}

/* ------------------------------------------------------------------------- */
/* documentele                                                               */
/* ------------------------------------------------------------------------- */

function Documents({ id, initial }: { id: number; initial: SummaryDocument[] }) {
  const [docs, setDocs] = useState(initial)

  const remove = async (doc: SummaryDocument) => {
    if (!window.confirm('Are you sure?')) return
    try {
      await removeDocument(id, doc.id)
      setDocs((list) => list.filter((d) => d.id !== doc.id))
    } catch {
      /* summary.php nu spune nimic la o scoatere picata: randul ramane. */
    }
  }

  return (
    <div className="row">
      <div className="col-md-12 divider">
        <h4 className="bordered">
          <strong>Device</strong> documents
        </h4>
      </div>
      <div className="col-md-12 ">
        <table className="table table-striped centered docsList">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Size</th>
              <th>Uploaded</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {docs.length === 0 ? (
              <tr>
                <td colSpan={5}>No documents...</td>
              </tr>
            ) : (
              docs.map((d) => (
                <tr key={d.id}>
                  <td>
                    <i className="fa fa-file-alt"></i> {d.name}
                  </td>
                  <td>{d.type}</td>
                  <td>{d.size}</td>
                  <td>{d.uploaded}</td>
                  <td>
                    <a href={d.download} className="btn btn-primary btn-xs">
                      <i className="fa fa-download"></i> Download
                    </a>{' '}
                    <button className="btn btn-danger btn-xs removeDoc" data-doc={d.id} onClick={() => void remove(d)}>
                      <i className="fas fa-times-circle"></i> Remove
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------------- */
/* pagina                                                                    */
/* ------------------------------------------------------------------------- */

function Loaded({ page, open }: { page: DeviceSummaryPage; open(href: string): void }) {
  const { id } = page.device
  const [pictureOpen, setPictureOpen] = useState(false)
  const picture = page.picture?.src ?? ''

  return (
    <>
      <div className="row row-content sum-tiles">
        {page.tiles.slice(0, 5).map((t, i) => (
          <Tile key={i} tile={t} id={id} open={open} />
        ))}
      </div>
      <div className="row row-content sum-tiles">
        {page.tiles.slice(5).map((t, i) => (
          <Tile key={i} tile={t} id={id} open={open} />
        ))}
      </div>

      {/* Linia de taxonomie din summary.php (cec-master 26.9.9.39): tipul pe
          care il cauta cineva cand deschide fisa, nu campuri rasfirate. */}
      {(page.taxonomyPath ?? []).length > 0 && (
        <div className="row row-content">
          <div className="col-md-12">
            <style>{`
.sum-taxonomy { margin: 10px 0 18px; padding: 10px 14px; background: #f4f6f8; border-left: 3px solid #5b6f8a; font-size: 14px; line-height: 1.45; color: #2c3e50; word-break: break-word; }
.sum-taxonomy .sum-tax-sep { margin: 0 .35em; color: #8a97a8; font-weight: 400; }
.sum-taxonomy .sum-tax-part { font-weight: 600; }
.sum-taxonomy .sum-tax-part:last-child { color: #1a5276; }
`}</style>
            <div className="sum-taxonomy" title="Class → Type → Subtype → Role → Manufacturer → Model → OS → Version">
              {(page.taxonomyPath ?? []).map((part, i) => (
                <span key={i}>
                  {i > 0 && <span className="sum-tax-sep" aria-hidden="true">→</span>}
                  <span className="sum-tax-part">{part}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="row row-content">
        <div className="col-md-10">
          <div className="dv-facts">
            <DeviceName page={page} />
            <Owner page={page} />
            <div className="dv-grid">
              {page.facts.map((f) => (
                <Fact key={f.label} fact={f} />
              ))}
              <DevicePartOfApps id={id} />
            </div>
            <DeviceSqlDatabases id={id} />
          </div>
        </div>

        <div className="col-md-2">
          <table style={{ width: '100%' }} className="details-table">
            <tbody>
              <tr>
                <td colSpan={2}>
                  <div className="row">
                    <div className="col-md-12 divider" style={{ marginTop: '0px' }}>
                      <h4 className="bordered">
                        <strong>QR</strong> Code
                      </h4>
                    </div>
                  </div>
                </td>
              </tr>
              <tr>
                <td colSpan={2} className="qr-code">
                  {page.qr ? <img src={page.qr.src} alt="QR code" width={page.qr.width} /> : 'no qr'}
                </td>
              </tr>
              {picture !== '' && (
                <>
                  <tr>
                    <td colSpan={2}>
                      <div className="row">
                        <div className="col-md-12 divider" style={{ marginTop: '0px' }}>
                          <h4 className="bordered">
                            <strong>Device</strong> Picture
                          </h4>
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2}>
                      <a
                        href="#"
                        data-toggle="modal"
                        data-target="#devicePictureModal"
                        onClick={(ev) => {
                          ev.preventDefault()
                          setPictureOpen(true)
                        }}
                      >
                        <img src={picture} style={{ width: '100%' }} />
                      </a>
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Documents id={id} initial={page.documents} />

      <Modal id="devicePictureModal" open={pictureOpen} onClose={() => setPictureOpen(false)} title="Device Picture" staticBackdrop keyboard={false}>
        {picture !== '' && <img src={picture} style={{ width: '100%' }} />}
      </Modal>
    </>
  )
}

export function DeviceSummary({ id, open }: ScreenProps) {
  /* Fisa sta in randul unei liste, sub orice modul: versiunea foii vine din scheletul deja cerut. */
  const shell = useQuery(shellQuery(moduleOf(typeof window === 'undefined' ? '' : window.location.pathname)))
  usePageStyles(STYLES, shell.data?.version ?? '')
  const page = useQuery(summaryQuery(id))

  if (page.isPending) return <Loading />
  if (page.isError) return <div className="panel-body color-light-grey">{page.error.message}</div>

  return <Loaded page={page.data} open={open} />
}
